import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSettings } from '@/contexts/SettingsContext';
import { motion } from 'framer-motion';
import { Lock, ArrowLeft, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { authApi } from '@/services/authApi';

export const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);
  const { user } = useAuth();
  const { getPrimaryColor, getFavicon, getLogo, getAuthPageLogo, getTitle } = useSettings();

  // Helper function to darken a color
  const darkenColor = (color: string, percent: number): string => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
    const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
    const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Get primary color and calculate darker shades for gradient
  const primaryColor = getPrimaryColor();
  const faviconUrl = getFavicon();
  const logoUrl = getLogo();
  const authPageLogoUrl = getAuthPageLogo();
  const portalTitle = getTitle();
  const gradientColors = React.useMemo(() => {
    const darker = darkenColor(primaryColor, 25);
    const darkest = darkenColor(primaryColor, 45);
    return {
      start: primaryColor,
      end: darkest,
      mid: darker,
    };
  }, [primaryColor]);

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/forgot-password');
    }
  }, [token, navigate]);

  const validatePassword = (password: string) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    setIsLoading(true);
    
    try {
      await authApi.resetPassword(token, password);
      // Redirect to login with success message
      navigate('/login', { state: { message: 'Password reset successful. Please login with your new password.' } });
    } catch (error: any) {
      console.error('Reset password failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to reset password. The link may have expired. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-vh-100 position-relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)` }}>
      {/* Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100">
        <div className="position-absolute" style={{ top: '10%', left: '5%', width: '200px', height: '200px', background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)`, borderRadius: '20px', transform: 'rotate(15deg)', opacity: 0.9 }}></div>
        <div className="position-absolute" style={{ top: '20%', right: '10%', width: '150px', height: '150px', background: `linear-gradient(135deg, ${gradientColors.end} 0%, ${gradientColors.mid} 100%)`, borderRadius: '50%', opacity: 0.9 }}></div>
        <div className="position-absolute" style={{ bottom: '20%', left: '15%', width: '100px', height: '100px', background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)`, borderRadius: '15px', transform: 'rotate(-30deg)', opacity: 0.9 }}></div>
        <div className="position-absolute" style={{ bottom: '30%', right: '20%', width: '120px', height: '120px', background: `linear-gradient(135deg, ${gradientColors.mid} 0%, ${gradientColors.end} 100%)`, borderRadius: '25px', transform: 'rotate(45deg)', opacity: 0.9 }}></div>
      </div>

      <div className="container position-relative" style={{ zIndex: 10 }}>
        <div className="row justify-content-center min-vh-100 align-items-center">
          <div className="col-lg-10 col-xl-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="position-relative"
            >
              {/* Main Content Container */}
              <div className="row g-0 shadow-lg rounded-4 overflow-hidden bg-white" style={{ minHeight: '600px' }}>
                {/* Left Side - Branding */}
                <div className="col-lg-6 position-relative d-none d-lg-flex" style={{ background: 'linear-gradient(135deg, var(--primary, #1e40af) 0%, var(--primary-dark, #1e3a8a) 100%)' }}>
                  <div className="position-relative p-3 p-md-4 p-lg-5 h-100 d-flex flex-column justify-content-center text-white" style={{ overflow: 'auto' }}>
                    <div className="text-center mb-4 mb-md-5">
                      {/* Logo */}
                      <div className="d-flex align-items-center justify-content-center mx-auto mb-3 mb-md-4" style={{ maxWidth: '100%', padding: '0 1rem' }}>
                        {logoError ? (
                          <span className="fs-1 fw-bold text-white" style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)' }}>{portalTitle}</span>
                        ) : (
                          <img 
                            src={authPageLogoUrl || faviconUrl || '/scholar_vest_logo.png'} 
                            alt={`${portalTitle} Agent Portal Logo`} 
                            style={{ 
                              height: "auto",
                              width: "auto",
                              maxHeight: "clamp(40px, 8vw, 80px)",
                              maxWidth: "100%",
                              objectFit: "contain",
                              objectPosition: "center"
                            }}
                            onError={() => setLogoError(true)}
                          />
                        )}
                      </div>
                      <p className="fs-5 text-white text-opacity-90">Agent Portal</p>
                      <h6 className="fs-2 fw-bold mb-3">Reset Your Password</h6>
                    </div>
                    
                    <div className="text-center">
                      <p className="fs-5 text-white text-opacity-90 mb-0">
                        Enter your new password below
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Side - Form */}
                <div className="col-lg-6 p-3 p-md-4 p-lg-5 d-flex flex-column justify-content-center" style={{ overflow: 'auto' }}>
                  <div className="w-100">
                    {/* Logo for mobile */}
                    <div className="text-center mb-4 d-lg-none" style={{ padding: '0 0.5rem' }}>
                      <div className="d-inline-flex align-items-center justify-content-center mb-3" style={{ maxWidth: '100%' }}>
                        {logoError ? (
                          <span className="fs-2 fw-bold text-primary" style={{ fontSize: 'clamp(1.25rem, 5vw, 2rem)' }}>{portalTitle}</span>
                        ) : (
                          <img 
                            src={authPageLogoUrl || logoUrl || '/scholar_vest_logo_full.png'} 
                            alt={`${portalTitle} Agent Portal Logo`} 
                            style={{ 
                              height: "auto",
                              width: "auto",
                              maxHeight: "clamp(40px, 10vw, 70px)",
                              maxWidth: "100%",
                              objectFit: "contain",
                              objectPosition: "center"
                            }}
                            onError={() => setLogoError(true)}
                          />
                        )}
                      </div>
                      <h2 className="fs-3 fw-bold text-dark mb-1">Agent Portal</h2>
                    </div>

                    <div className="text-center mb-4">
                      <h3 className="fs-2 fw-bold text-dark mb-2">Reset Password</h3>
                      <p className="text-muted">Enter your new password below</p>
                    </div>

                    {error && (
                      <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
                        <AlertCircle size={18} className="me-2" />
                        <small>{error}</small>
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      {/* Password */}
                      <div className="mb-3">
                        <label className="form-label fw-semibold text-dark">New Password</label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <Lock size={18} className="text-muted" />
                          </span>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            className="form-control border-start-0"
                            name="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter new password"
                            required
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary border-start-0"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      {/* Confirm Password */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold text-dark">Confirm New Password</label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <Lock size={18} className="text-muted" />
                          </span>
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            className="form-control border-start-0"
                            name="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            required
                          />
                          <button
                            type="button"
                            className="btn btn-outline-secondary border-start-0"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          >
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            Resetting...
                          </>
                        ) : (
                          <>
                            Reset Password
                            <ArrowLeft size={18} />
                          </>
                        )}
                      </button>

                      {/* Back to Login Link */}
                      <div className="text-center">
                        <Link 
                          to="/login" 
                          className="d-inline-flex align-items-center text-decoration-none"
                          style={{ color: primaryColor, fontSize: '16px', fontWeight: '500' }}
                        >
                          <ArrowLeft size={18} className="me-2" />
                          Back to Login
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

